"use client";

import { PageHeader } from "@/components/ui/page-header";
import { useState, type FormEvent } from "react";

export default function FeatureRequestPage() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "feature-request", title, description }),
      });
      if (!res.ok) throw new Error("Failed");
      setStatus("success");
      setTitle(""); setDescription("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="container py-12 md:py-16">
      <div className="mx-auto max-w-2xl">
        <PageHeader title="Request a Feature" />
        <p className="mt-2 text-lg text-[var(--color-text-muted)]">
          Help shape the future of DevStackIO.
        </p>
        <p className="mt-4 text-sm text-[var(--color-text-muted)]">
          Tell us which developer tool or improvement would make your workflow easier. Every request is
          reviewed against our roadmap and community demand &mdash; ideas that help many developers are
          prioritized. Please be specific about the problem you&apos;re trying to solve so we can evaluate it
          accurately.
        </p>

        {status === "success" ? (
          <div className="mt-8 rounded-md border border-[var(--color-success-200)] bg-[var(--color-success-50)] p-6 text-center">
            <p className="font-medium text-[var(--color-success-700)]">Thank you! Your feature request has been received.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">Feature Title</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required
                placeholder="e.g., Dark mode toggle"
                className="mt-1 flex h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] border-[var(--color-border)]  text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} required rows={4}
                placeholder="Describe the feature and how it would benefit you..."
                className="mt-1 flex w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] border-[var(--color-border)]  text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)]" />
            </div>
            {status === "error" && (
              <p className="text-sm text-[var(--color-error-600)]">Failed to submit. Please try again.</p>
            )}
            <button type="submit" disabled={status === "loading"}
              className="inline-flex w-full items-center justify-center rounded-md bg-[var(--color-accent)] px-6 py-3 text-sm font-medium text-[var(--color-accent-fg)] hover:bg-[var(--color-accent-hover)] disabled:opacity-50 transition-colors">
              {status === "loading" ? "Submitting..." : "Submit Request"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
